<script setup lang="ts">
import { computed, onMounted } from "vue"
import { useRoute } from "vue-router"
import DOMPurify from "dompurify"
import { useApi } from "../composables/useApi"
import { API_BASE } from "../config"
import { formatJobDesc } from "../utils"

const route = useRoute()
const { data: job, loading, error, execute } = useApi()

// Prefer the server-enriched, pre-sanitized jobdesc_html when present;
// still pass it through DOMPurify as client-side defense-in-depth. Fall
// back to formatting the plain-text jobdesc when no HTML is available.
const sanitizedJobDesc = computed(() =>
  DOMPurify.sanitize(
    job.value?.jobdesc_html || formatJobDesc(job.value?.jobdesc ?? ""),
  )
)

onMounted(() => {
  const id = route.params.id
  execute(`${API_BASE}/job_listings/${id}.json`)
})
</script>

<template>
    <section class="container" aria-label="Job detail">
        <div v-if="error" class="notification is-danger is-light">
            <button
class="delete"
aria-label="Dismiss error"
@click="error = null"></button>
            Failed to load job details. Please try again.
        </div>
        <div v-if="loading" class="has-text-centered p-6 loading-state">
            <span class="button is-loading is-large is-ghost"></span>
            <p class="mt-2">Loading job details...</p>
        </div>
        <template v-else>
        <div class="box">
            <div class="header">
                <h1 class="title is-1 has-text-centered">Job Post!</h1>
                <table
class="
        table
        is-bordered is-striped is-narrow is-hoverable is-fullwidth
        ">
                    <tbody>
                        <tr>
                            <td>Organization</td>
                            <td class="has-text-centered">{{ job?.organization }}</td>
                        </tr>
                        <tr>
                            <td>Location</td>
                            <td class="has-text-centered">{{ job?.location }}</td>
                        </tr>
                        <tr>
                            <td>Date</td>
                            <td class="has-text-centered">{{ job?.post_date }}</td>
                        </tr>
                        <tr v-if="job?.salary_display">
                            <td>Compensation</td>
                            <td class="has-text-centered">
                                {{ job.salary_display }}
                                <span
v-if="job.salary_is_from_description"
class="is-size-7 has-text-grey">({{ job.salary_source_label }})</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
        <div class="box">
            <h4
id="job-detail-title"
class="title is-4">Job Title: {{ job?.jobtitle }}</h4>
            <div
id="JobPostDetailedView"
class="container content"
role="region"
aria-labelledby="job-detail-title"
v-html="sanitizedJobDesc"></div>
        </div>
        <div
class="action">
            <button
class="button is-primary"
aria-label="Back to results"
@click="$router.go(-1)">
                <i class="fas fa-arrow-left" aria-hidden="true"></i>
                <span class="is-sr-only">Back to results</span>
                Finished? Return to listing
            </button>
            <a
v-if="job?.url_text !== null"
:href="job?.url_text"
class="button is-link"
target="_blank">View Source</a>
        </div>
        </template>
    </section>
</template>

<style scoped>
.action {
  text-align: center;
  padding: 1.5rem 0;
}
</style>
